import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { Globe, Plus, Search } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { ApiError } from "@hostdock/sdk";
import { Button, Checkbox, DataTable, Dialog, EmptyState, Field, FormError, formatBytes, formatDate, Input, Notice, PageHeader, Pagination, Panel, Select } from "@hostdock/ui";
import { api } from "../../api";
import { RequirePermission } from "../../lib/guard";
import { useListParams } from "../../lib/list-params";
import { useSession } from "../../session";
import { runtimeLabel, runtimeOptionLabel, SslBadge, WebsiteStatusBadge } from "./shared";

const PAGE_SIZE = 50;

export function WebsitesPage({ apps = false }: { apps?: boolean }) {
  return (
    <RequirePermission anyOf={["WEBSITE_VIEW"]}>
      <Websites apps={apps} />
    </RequirePermission>
  );
}

function Websites({ apps }: { apps: boolean }) {
  const { can } = useSession();
  const navigate = useNavigate();
  const { values, page, set } = useListParams(["search", "status"] as const);
  const [search, setSearch] = useState(values.search);
  const [creating, setCreating] = useState(false);
  const q = useQuery({
    queryKey: ["websites", apps, values, page],
    queryFn: () => api.websites.list({ ...values, ...(apps ? { runtime: "apps" } : {}), page, pageSize: PAGE_SIZE }),
    placeholderData: keepPreviousData,
    refetchInterval: 10_000,
  });
  return (
    <>
      <PageHeader
        title={apps ? "Applications" : "Websites"}
        description={apps ? "Node.js and Python applications." : "Every hosted website and application, across all regions."}
        actions={can("WEBSITE_MANAGE") && <Button variant="primary" icon={<Plus size={15} />} onClick={() => setCreating(true)}>New website</Button>}
      />
      <Panel flush>
        <div className="flex flex-wrap items-center gap-2 border-b border-line p-3">
          <form className="relative min-w-52 flex-1" onSubmit={(e) => { e.preventDefault(); set({ search: search.trim() }); }}>
            <Search size={14} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-subtle" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Domain or customer" className="pl-8" aria-label="Search websites" />
          </form>
          <Select value={values.status} onChange={(e) => set({ status: e.target.value })} className="w-40" aria-label="Status">
            <option value="">All statuses</option>
            {["ACTIVE", "PROVISIONING", "SUSPENDED", "FAILED", "DELETING"].map((s) => <option key={s} value={s}>{s.charAt(0) + s.slice(1).toLowerCase()}</option>)}
          </Select>
        </div>
        <DataTable
          rows={q.data?.items}
          loading={q.isPending}
          error={q.error}
          onRetry={() => void q.refetch()}
          rowKey={(w) => w.id}
          onRowClick={(w) => navigate(`/hosting/websites/${w.id}`)}
          empty={<EmptyState icon={Globe} title={values.search || values.status ? "No matching websites" : "No websites yet"} description={values.search || values.status ? undefined : "Websites appear here when customers or admins create them."} />}
          columns={[
            { key: "domain", header: "Domain", cell: (w) => <div className="min-w-0"><p className="truncate font-medium">{w.primaryDomain}</p><p className="truncate text-[12px] text-muted">{w.customer.displayName}</p></div> },
            { key: "status", header: "Status", cell: (w) => <WebsiteStatusBadge status={w.status} /> },
            { key: "region", header: "Region", cell: (w) => <span className="text-muted">{w.region.name}{w.server ? <span className="font-mono text-[11.5px]"> · {w.server.name}</span> : null}</span>, hideOnMobile: true },
            { key: "runtime", header: "Runtime", cell: (w) => runtimeLabel(w.runtime, w.runtimeVersion) },
            { key: "ssl", header: "SSL", cell: (w) => <SslBadge status={w.ssl} />, hideOnMobile: true },
            { key: "storage", header: "Storage", cell: (w) => <span className="tabular-nums text-muted">{w.diskBytes === null ? "—" : formatBytes(w.diskBytes)}</span>, hideOnMobile: true },
            { key: "created", header: "Created", cell: (w) => <span className="text-muted">{formatDate(w.createdAt)}</span>, hideOnMobile: true },
          ]}
        />
        {q.data && <Pagination page={page} pageSize={PAGE_SIZE} total={q.data.total} onPage={(p) => set({ page: String(p) })} />}
      </Panel>
      {creating && <CreateWebsiteDialog onClose={() => setCreating(false)} onCreated={(id) => navigate(`/hosting/websites/${id}`)} />}
    </>
  );
}

function CreateWebsiteDialog({ onClose, onCreated }: { onClose(): void; onCreated(id: string): void }) {
  const { can } = useSession();
  const subs = useQuery({ queryKey: ["subscriptions-all"], queryFn: api.subscriptions.all });
  const regions = useQuery({ queryKey: ["regions"], queryFn: api.regions.list });
  const settings = useQuery({ queryKey: ["settings"], queryFn: api.settings.get });
  const plans = useQuery({ queryKey: ["plans"], queryFn: api.plans.list });
  const [f, setF] = useState({ subscriptionId: "", regionId: "", appType: "PHP" as "WORDPRESS" | "WOOCOMMERCE" | "PHP" | "NODE" | "PYTHON" | "STATIC", domain: "", runtimeVersion: "", database: "", redis: false, wwwAlias: true, serverId: "" });
  const [wpf, setWpf] = useState({ title: "", adminUsername: "", adminEmail: "" });
  const wordpress = f.appType === "WORDPRESS" || f.appType === "WOOCOMMERCE";
  const isStatic = f.appType === "STATIC";
  const servers = useQuery({ queryKey: ["servers-region", f.regionId], queryFn: () => api.servers.list({ regionId: f.regionId, status: "ACTIVE", pageSize: 100 }), enabled: Boolean(f.regionId) && can("SERVER_MANAGE") });
  const sub = subs.data?.items.find((s) => s.id === f.subscriptionId);
  const plan = plans.data?.items.find((p) => p.id === sub?.plan.id);
  // A static site has no container runtime; the value is only used by the non-static branches below.
  const runtime = f.appType === "NODE" ? "node" : f.appType === "PYTHON" ? "python" : "php";
  const versions = isStatic ? [] : (settings.data?.runtimes[runtime] ?? []).filter((v) => !plan || plan.runtimes[runtime].length === 0 || plan.runtimes[runtime].includes(v));
  const regionOptions = (regions.data?.items ?? []).filter((r) => r.status === "ACTIVE" && (!plan || plan.regionIds.length === 0 || plan.regionIds.includes(r.id)));
  const create = useMutation({ meta: { inlineError: true },
    mutationFn: () => api.websites.create({
      subscriptionId: f.subscriptionId, regionId: f.regionId, appType: f.appType, domain: f.domain, runtimeVersion: isStatic ? "" : (f.runtimeVersion || versions[versions.length - 1]!),
      database: wordpress ? { engine: "MARIADB" } : (!isStatic && f.database) ? { engine: f.database as "MARIADB" } : null, redis: isStatic ? false : f.redis, wwwAlias: f.wwwAlias, serverId: f.serverId || null,
      ...(wordpress ? { wordpress: { title: wpf.title || f.domain, adminUsername: wpf.adminUsername, adminEmail: wpf.adminEmail } } : {}),
    }),
    onSuccess: (r) => onCreated(r.website.id),
  });
  const fe = create.error instanceof ApiError ? create.error.fieldErrors : {};
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="New website"
      description="HostDock selects a node in the region, reserves resources and deploys the site."
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" loading={create.isPending} disabled={!f.subscriptionId || !f.regionId || !f.domain || (!isStatic && !versions.length) || (wordpress && (!wpf.adminUsername || !wpf.adminEmail))} onClick={() => create.mutate()}>Create website</Button></>}
    >
      <div className="flex flex-col gap-4">
        {subs.data && subs.data.items.length === 0 && <Notice tone="warning" title="No subscriptions">Assign a plan to a customer first (Customers → customer → Subscriptions).</Notice>}
        <Field label="Subscription" error={fe.subscriptionId}>
          {(p) => (
            <Select {...p} value={f.subscriptionId} onChange={(e) => setF({ ...f, subscriptionId: e.target.value, regionId: "", runtimeVersion: "" })}>
              <option value="">Select customer subscription…</option>
              {subs.data?.items.filter((s) => s.status === "ACTIVE").map((s) => <option key={s.id} value={s.id}>{s.customerName} — {s.plan.name} ({s.usage.websites} site{s.usage.websites === 1 ? "" : "s"})</option>)}
            </Select>
          )}
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Region" error={fe.regionId}>
            {(p) => <Select {...p} value={f.regionId} onChange={(e) => setF({ ...f, regionId: e.target.value, serverId: "" })}><option value="">Select…</option>{regionOptions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</Select>}
          </Field>
          <Field label="Application">
            {(p) => (
              <Select {...p} value={f.appType} onChange={(e) => setF({ ...f, appType: e.target.value as "PHP", runtimeVersion: "" })}>
                <option value="WORDPRESS">WordPress</option><option value="WOOCOMMERCE">WooCommerce</option>
                <option value="PHP">PHP</option><option value="NODE">Node.js</option><option value="PYTHON">Python</option>
                <option value="STATIC">Start From Scratch (static)</option>
              </Select>
            )}
          </Field>
          <Field label="Domain" hint="The site is served over HTTP until DNS is verified, then HTTPS." error={fe.domain}>{(p) => <Input {...p} value={f.domain} onChange={(e) => setF({ ...f, domain: e.target.value.trim().toLowerCase() })} placeholder="example.com" />}</Field>
          {!isStatic && (
          <Field label="Runtime version" error={fe.runtimeVersion}>
            {(p) => <Select {...p} value={f.runtimeVersion || versions[versions.length - 1] || ""} onChange={(e) => setF({ ...f, runtimeVersion: e.target.value })}>{versions.map((v) => <option key={v} value={v}>{runtimeOptionLabel(runtime, v)}</option>)}</Select>}
          </Field>
          )}
          {wordpress ? (
            <>
              <Field label="Site title">{(p) => <Input {...p} value={wpf.title} onChange={(e) => setWpf({ ...wpf, title: e.target.value })} placeholder={f.domain || "Site title"} />}</Field>
              <Field label="WordPress administrator" error={fe["wordpress.adminUsername"]}>{(p) => <Input {...p} value={wpf.adminUsername} onChange={(e) => setWpf({ ...wpf, adminUsername: e.target.value.trim().toLowerCase() })} placeholder="username" autoComplete="off" />}</Field>
              <Field label="Administrator email" error={fe["wordpress.adminEmail"]}>{(p) => <Input {...p} type="email" value={wpf.adminEmail} onChange={(e) => setWpf({ ...wpf, adminEmail: e.target.value.trim() })} />}</Field>
            </>
          ) : isStatic ? null : (
          <Field label="Database">
            {(p) => (
              <Select {...p} value={f.database} onChange={(e) => setF({ ...f, database: e.target.value })}>
                <option value="">None</option><option value="MARIADB">MariaDB</option><option value="POSTGRESQL">PostgreSQL</option>
                {plan?.features.mongodb && <option value="MONGODB">MongoDB</option>}
              </Select>
            )}
          </Field>
          )}
          {can("SERVER_MANAGE") && (
            <Field label="Node (optional)" hint="Leave on automatic unless you need a specific node.">
              {(p) => <Select {...p} value={f.serverId} onChange={(e) => setF({ ...f, serverId: e.target.value })} disabled={!f.regionId}><option value="">Automatic (scheduler)</option>{servers.data?.items.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select>}
            </Field>
          )}
        </div>
        <div className="flex flex-wrap gap-5">
          <Checkbox checked={f.wwwAlias} onChange={(v) => setF({ ...f, wwwAlias: v })} label="Also serve www." />
          {!isStatic && plan?.features.redis && <Checkbox checked={f.redis} onChange={(v) => setF({ ...f, redis: v })} label="Enable Redis" />}
        </div>
        {create.error && !Object.keys(fe).length ? <FormError error={create.error} /> : null}
      </div>
    </Dialog>
  );
}
